<template>
  <div class="debt-payment-root">

    <!-- Loading Overlay -->
    <transition name="fade">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-card">
          <b-spinner variant="primary" style="width:2rem;height:2rem;" class="mb-2" />
          <p class="loading-text">Memuat data utang...</p>
        </div>
      </div>
    </transition>

    <!-- Header -->
    <header class="sticky-top border border-light-bottom border-light-line border-light-top border-light-line p-2 dp-header">
      <b-row>
        <b-col cols="3" class="d-flex align-items-center">
          <div style="cursor: pointer" class="back-btn" @click="hideModals()">
            <feather-icon size="24" class="text-dark font-weight-bolder" icon="ArrowLeftIcon" />
            <span class="text-dark text-darken-4 fw-bold-600 size14 ml-1">Kembali</span>
          </div>
        </b-col>
        <b-col cols="6" class="d-flex justify-content-center align-items-center flex-column">
          <h1 class="text-dark h6 font-weight-bolder mb-0 text-center">
            {{ details.name || "-" }}
          </h1>
          <small class="text-muted fw-bold-500 size12">
            Telp : {{ details.phone_number || "-" }}
          </small>
        </b-col>
        <b-col cols="3" class="d-flex flex-column align-items-end justify-content-center">
          <small class="text-muted" style="font-size:11px;">Total Utang</small>
          <span class="font-weight-bold text-dark" style="font-size:14px;">
            {{ (details.utang_total - details.utang_paid) | formatAmount }}
          </span>
        </b-col>
      </b-row>
    </header>

    <!-- Main Content -->
    <b-container fluid id="utang" class="mt-5 pt-5 mb-3" style="max-height: calc(100vh - 220px); overflow-y: auto;">
      <b-row class="mx-0 mt-1 justify-content-center">
        <b-col class="" cols="12">
          <b-table
            style="margin-bottom:10rem"
            responsive="sm"
            :fields="fields"
            :items="items"
            :tbody-tr-class="rowClass"
            class="dp-table"
          >
            <!-- Heads -->
            <template #head(action)="">
              <div class="custom-control custom-checkbox">
                <input id="check-all" class="custom-control-input" type="checkbox"
                  :checked="isCurrentPageFullySelected" @change="checkedAll(items)" />
                <label for="check-all" class="custom-control-label"> </label>
              </div>
            </template>
            <template #head(branch)=""><span>Cabang</span></template>
            <template #head(no_po)=""><span>No. PO</span></template>
            <template #head(delivery_numbers)=""><span>No. Surat Jalan</span></template>
            <template #head(unpaid)=""><span>Nilai Utang</span></template>
            <template #head(due_date)=""><span>Tgl Jatuh Tempo</span></template>
            <template #head(no_invoice)=""><span>No. Invoice</span></template>
            <template #head(factur_number_tax)=""><span>No. Faktur Pajak Pemasok</span></template>
            <template #head(due_date_info)=""><span>Keterangan</span></template>
            <template #head(amount)=""><span>Bayar (Rp)</span></template>
            <template #head(voucher)=""><span /></template>

            <!-- Cells -->
            <template #cell(action)="data">
              <b-form-checkbox
                v-model="formPayload.items[data.index].uuid"
                :value="data.item.uuid"
                @change="handleSelectionChange(data.index)"
              />
            </template>

            <template #cell(no_po)="data">
              <span class="text-blue" role="button"
                @click="detailPreOrder(data.item.uuid_po, data.item.is_stock_on_supplier)">
                {{ data.item.no_po || "-" }}
              </span>
            </template>

            <template #cell(delivery_numbers)="data">
              <div v-if="data.item.delivery_numbers">
                <span v-if="data.item.delivery_numbers.length < 3">
                  {{ data.item.delivery_numbers.map(e => e.delivery_number_note).slice(0,3).join(", ") }}
                </span>
                <b-dropdown v-else
                  :text="data.item.delivery_numbers.map(e => e.delivery_number_note).slice(0,3).join(', ')"
                  variant="flat-secondary" class="delivery-numbers px-0">
                  <b-row class="mx-0 pt-1">
                    <b-col v-for="(item, index) in data.item.delivery_numbers" :key="index" class="">
                      <h6 class="text-dark">{{ item.delivery_number_note }}</h6>
                    </b-col>
                  </b-row>
                </b-dropdown>
              </div>
            </template>

            <template #cell(unpaid)="data">
              <span class="unpaid-val">{{ formatAmountWithDecimal(data.item.unpaid) }}</span>
            </template>

            <template #cell(due_date_info)="data">
              <span :class="dueBadgeClass(data.item.due_date_info)" class="due-info-badge">
                {{ data.item.due_date_info || '-' }}
              </span>
            </template>

            <template #cell(voucher)="data">
              <b-dropdown offset="-230" :dropup="true"
                variant="outline-secondary bg-white p-1" class="btn-filter"
                :disabled="!formPayload.items[data.index].uuid">
                <template #button-content>
                  <b-img v-if="!formPayload.items[data.index].voucher_uuid.length"
                    :src="require(`@/assets/images/icons/coupon.svg`)" />
                  <b-img v-else :src="require(`@/assets/images/icons/coupon-active.svg`)" />
                </template>
                <b-dropdown-form class="p-0">
                  <b-form-group label-for="dropdown-form-voucher" class="mb-0 p-0" style="width:280px" @submit.prevent>
                    <v-select
                      :value="vouchers[data.index]"
                      placeholder="Pilih Voucher"
                      label="voucher_code"
                      :options="options"
                      multiple
                      @search="onSearch"
                      @input="handleVoucherChange(data.index, $event)"
                    />
                  </b-form-group>
                </b-dropdown-form>
              </b-dropdown>
            </template>

            <template #cell(no_invoice)="data">
              <b-form-group label="" label-for="v-no-invoice" class="custom__form--input mb-0">
                <b-form-input
                  v-model="formPayload.items[data.index].invoice_number"
                  :disabled="!formPayload.items[data.index].uuid"
                  type="text"
                  @input="syncSelectedItem(data.index)"
                />
              </b-form-group>
            </template>

            <template #cell(factur_number_tax)="data">
              <b-form-group label="" label-for="v-faktur" class="custom__form--input mb-0">
                <b-form-input
                  v-model="formPayload.items[data.index].factur_number_tax"
                  :disabled="!formPayload.items[data.index].uuid"
                  type="text"
                  @input="syncSelectedItem(data.index)"
                />
              </b-form-group>
            </template>

            <template #cell(amount)="data">
              <b-form-group label="" label-for="v-bayar" class="custom__form--input mb-0">
                <Cleave
                  v-model="formPayload.items[data.index].amount"
                  :raw="true"
                  :options="optionNumber"
                  class="custom__input pl-2"
                  @input="handleAmountChange(data.index)"
                />
              </b-form-group>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Footer -->
      <div class="footer__purchase p-3" style="position:fixed; width:100%;">
        <b-row class="align-items-center">

          <!-- LEFT -->
          <b-col lg="4">
            <h5 class="mb-1 text-dark fw-bold-600">
              Total Nilai Utang
              {{ (details.utang_total - details.utang_paid) | formatAmount }}
            </h5>

            <b-badge variant="primary">
              Total {{ selectedInCurrentPage + selectedInOtherPages }} Tagihan Dipilih
              <!-- <template v-if="selectedInOtherPages > 0">
                ({{ selectedInCurrentPage }} di halaman ini, {{ selectedInOtherPages }} di halaman lain)
              </template> -->
            </b-badge>

            <b-button
              v-if="Object.keys(selectedItems).length"
              size="sm"
              variant="outline-danger"
              class="ml-2"
              @click="resetSelection"
            >
              Reset
            </b-button>
          </b-col>

          <!-- CENTER -->
          <b-col lg="4" class="d-flex justify-content-center align-items-center">
            <span class="mr-2 text-muted">Tampilkan</span>
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              size="sm"
              style="width:90px"
              @change="handlePerPageChange"
            />
            <span class="ml-2 text-muted">data</span>
          </b-col>

          <!-- RIGHT -->
          <b-col lg="4">
            <div class="d-flex justify-content-end align-items-center">
              <pagination
                v-if="pagination"
                :data="pagination"
                :limit="3"
                class="mr-3 mb-0"
                @pagination-change-page="getDetailUtang"
              />

              <b-button
                v-if="checkPermission('simpan pembayaran utang')"
                class="btn-save-draft py-2 mr-2"
                @click="savePayment"
              >
                Simpan
              </b-button>

              <b-button
                v-if="checkPermission('bayar pembayaran utang')"
                class="btn-pay-action py-2"
                :disabled="!Object.keys(selectedItems).length"
                @click="handleBayar"
              >
                Bayar {{ formatAmountWithDecimal(total + totalVoucher) }}
              </b-button>
            </div>
          </b-col>

        </b-row>
      </div>
    </b-container>

    <DetailSupplier v-if="result" :result="result" />
    <ModalPayment :total="total" :totalVoucher="totalVoucher" />
    <PaymentPreview
      :selected-items="selectedItems"
      :total="total"
      :total-voucher="totalVoucher"
      @confirm="openPaymentModal"
      @detail-po="detailPreOrder"
    />
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from "lodash";
import {
  BButton,
  BRow,
  BCol,
  BContainer,
  BTable,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BDropdown,
  BDropdownForm,
  BFormSelect,
  BBadge,
  BImg,
  VBTooltip,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import DetailSupplier from "@/components/Debt/Modal/Part/DetailSupplier.vue";
import ModalPayment from "@/components/Debt/Modal/ModalPayment.vue";
import PaymentPreview from "@/components/Debt/Modal/Part/PaymentPreview.vue";
import {
  checkPermission,
  successNotification,
  formatRupiah,
  preformatFloat,
} from "@/auth/utils";

import Cleave from "vue-cleave-component";

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BBadge,
    BContainer,
    BTable,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    DetailSupplier,
    BDropdown,
    BDropdownForm,
    BImg,
    BFormSelect,
    vSelect,
    ModalPayment,
    PaymentPreview,
    Cleave,
    BSpinner,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  setup() {
    return {
      checkPermission,
      successNotification,
      formatRupiah,
      preformatFloat,
    };
  },
  data() {
    return {
      selectedItems: {},
      voucher_coupon: "",
      fields: [
        "action",
        "branch",
        "no_po",
        "delivery_numbers",
        "unpaid",
        "due_date",
        "no_invoice",
        "factur_number_tax",
        "due_date_info",
        "amount",
        "voucher",
      ],
      items: [],
      details: {},
      pagination: null,
      loading: false,
      perPage: Number(localStorage.getItem("utang_per_page")) || 10,
      perPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50],
      formPayload: {
        items: [],
        payment_method: "",
      },
      total: 0,
      userData: JSON.parse(localStorage.getItem("userData")),
      result: {},
      options: [],
      detail_voucher: {},
      vouchers: [],
      totalVoucher: 0,
      oldValueVoucher: [],
      voucherOptions: [],
      supplier_uuid: localStorage.getItem("customer_supplier_uuid"),
      selectAllValue: [],
      checkAll: false,
      optionNumber: {
        numeral: true,
        numeralThousandsGroupStyle: "thousand",
        numeralDecimalScale: 2,
        numeralDecimalMark: ",",
        delimiter: ".",
      },
      currentBranch: "",
    };
  },
  computed: {
    isCurrentPageFullySelected() {
      return (
        this.items.length > 0 &&
        this.items.every((row) => this.selectedItems[row.uuid])
      );
    },
    selectedInCurrentPage() {
      return this.items.filter((row) => this.selectedItems[row.uuid]).length;
    },
    selectedInOtherPages() {
      return Object.keys(this.selectedItems).length - this.selectedInCurrentPage;
    },
  },
  watch: {
    voucher_coupon(value) {
      this.getDetailVoucher(value);
    },
    "$store.state.supplier.detailUtang": {
      handler(value) {
        this.details = value;
      },
      deep: true,
    },
  },
  created() {
    if (!checkPermission("bayar pembayaran utang")) {
      this.removeArrayByValue("amount");
      this.removeArrayByValue("voucher");
    }
  },
  async mounted() {
    const from = this.$route.query.from;
    if (from !== "report_healthcheck") {
      await this.loadDrafts();
    }

    await this.getVoucher();
    await this.getDetailUtang();
  },
  methods: {
    formatAmountWithDecimal(value) {
      return new Intl.NumberFormat("id-ID", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 10, // preserve existing decimals
      }).format(Number(value));
    },
    dueBadgeClass(info) {
      if (!info) return "";
      if (info.includes("< 14")) return "due-badge-warn";
      if (info.includes("14 - 30")) return "due-badge-danger";
      if (info.includes("> 30")) return "due-badge-critical";
      if (info.includes("Belum")) return "due-badge-safe";
      return "";
    },
    handleBayar() {
      const items = Object.values(this.selectedItems || {});
      const total = Math.ceil(items.reduce((sum, item) => {
        let raw = item.amount;
        let value;
        if (typeof raw === "number") {
          value = raw;
        } else if (typeof raw === "string") {
          value = Number(raw.replace(/\./g, ""));
        }
        return sum + (isNaN(value) ? 0 : value);
      }, 0));

      this.$store.commit("supplier/setTotal", total);
      this.$store.commit("supplier/setFormPayloadUtang", { items });
      this.$bvModal.show("modal-payment-preview");
    },
    openPaymentModal() {
      this.$bvModal.hide("modal-payment-preview");
      this.$bvModal.show("modal-payment");
    },
    resetSelection() {
      this.selectedItems = {};
      this.formPayload.items.forEach((item) => {
        item.uuid = null;
        item.is_checked = 0;
        item.invoice_number = "";
        item.factur_number_tax = "";
        item.voucher_uuid = [];
      });
      this.vouchers = this.vouchers.map(() => []);
      this.calculateTotal();
      this.calculateTotalVoucher();
    },
    populateRows(rows) {
      this.items = [];
      this.formPayload.items = [];
      this.vouchers = [];

      rows.forEach((element, index) => {
        const selected = this.selectedItems[element.uuid];
        const isSelected = !!selected;
        
        if (isSelected) {
          Object.assign(this.selectedItems[element.uuid], {
            no_po: element.reference?.po_so_id,
            branch: element.branch?.name,
            due_date: element.due_date,
            uuid_po: element.reference?.uuid,
            is_stock_on_supplier: element.reference?.is_stock_on_supplier,
          });
        }
        
        this.vouchers.push([]);
        this.formPayload.items.push({
          uuid: isSelected ? element.uuid : null,
          invoice_number:
            selected?.invoice_number && selected.invoice_number !== "undefined"
              ? selected.invoice_number
              : "",
          factur_number_tax:
            selected?.factur_number_tax &&
            selected.factur_number_tax !== "undefined"
              ? selected.factur_number_tax
              : "",
          amount:
            selected?.amount ??
            Math.ceil(element.unpaid),
            
          voucher_uuid: selected?.voucher_uuid ?? [],
          is_checked: isSelected ? 1 : 0,
          unpaid: Math.ceil(element.unpaid),
          original_unpaid: element.unpaid,
        });

        this.items.push({
          is_stock_on_supplier: element.reference?.is_stock_on_supplier,
          uuid_po: element.reference?.uuid,
          index,
          uuid: element.uuid,
          no_po: element.reference?.po_so_id,
          branch: element.branch?.name,
          delivery_numbers: element.reference?.delivery_numbers || [],
          unpaid: Math.ceil(element.unpaid),
          paid: element.paid,
          due_date: element.due_date,
          due_date_info: element.due_date_info,
        });
      });

      this.calculateTotal();
    },
    rowClass(item) {
      if (!item) return "";
      const info = item?.due_date_info ?? "";
      const itemBranch = item?.branch ?? "";
      const period = Number(this.$route.query.period);
      const matchBranch = itemBranch === this.currentBranch;
      if (!matchBranch) return "";
      const textMap = {
        1: "Lewat Jatuh Tempo < 14 Hari",
        2: "Lewat Jatuh Tempo 14 - 30 Hari",
        3: "Lewat Jatuh Tempo > 30 Hari",
        4: "Belum Jatuh Tempo",
      };
      return info.includes(textMap[period]) ? "table-success" : "";
    },
    hideModals() {
      this.$bvModal.hide("modal-debt");
    },
    checkedAll(items) {
      const filteredArray = this.getUniqueListBy(items, "uuid");
      if (!this.isCurrentPageFullySelected) {
        filteredArray.forEach((row, index) => {
          this.formPayload.items[index].uuid = row.uuid;
          this.formPayload.items[index].is_checked = 1;
          this.$set(this.selectedItems, row.uuid, {
            ...this.formPayload.items[index],
            uuid: row.uuid,
            is_checked: 1,
            no_po: row.no_po,
            branch: row.branch,
            due_date: row.due_date,
            uuid_po: row.uuid_po,
            is_stock_on_supplier: row.is_stock_on_supplier,
          });
        });
      } else {
        filteredArray.forEach((row, index) => {
          this.formPayload.items[index].uuid = null;
          this.formPayload.items[index].is_checked = 0;
          this.$delete(this.selectedItems, row.uuid);
        });
      }
      this.calculateTotal();
    },
    getUniqueListBy(arr, key) {
      return [...new Map(arr.map((item) => [item[key], item])).values()];
    },
    handlePerPageChange() {
      localStorage.setItem("utang_per_page", this.perPage);
      this.getDetailUtang(1);
    },
    syncSelectedItem(index) {
      const row = this.items[index];
      if (!this.selectedItems[row.uuid]) return;
      Object.assign(this.selectedItems[row.uuid], this.formPayload.items[index], {
        uuid: row.uuid,
      });
    },
    async getDetailUtang(page = 1) {
      this.loading = true;
      try {
        const supplier = this.$store.state.supplier.selectedSupplier;
        const params = { page, per_page: this.perPage };
        if (this.$route.query.from === "report.index" && this.$route.query.period) {
          params.period = this.$route.query.period;
        }
        const result = await this.$store.dispatch("supplier/getDetailUtang", {
          uuid: supplier.uuid,
          params,
        });
        const response = result.data.data.items;
        this.details = { ...supplier, ...result.data.data };
        this.items = response.data;
        this.pagination = response;
        this.populateRows(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async loadDrafts() {
      const { data } = await this.$http.get(
        `/utang/${this.supplier_uuid}/payment-drafts`
      );
      this.selectedItems = {};
      data.data.forEach((item) => {
        this.$set(this.selectedItems, item.uuid, item);
      });
      this.calculateTotal();
    },
    async savePayment() {
      try {
        delete this.formPayload.payment_method;
        const form = this.preparePayload();
        const response = await this.$http.post(
          `/utang/${this.supplier_uuid}/add-payment-draft`,
          form
        );
        if (response.status === 200) {
          successNotification(this, "Success", "Utang berhasil disimpan ke draft");
          this.$bvModal.hide("modal-debt");
        }
      } catch (error) {
        console.log(error);
      }
    },
    removeArrayByValue(x) {
      const index = this.fields.indexOf(x);
      if (index > -1) this.fields.splice(index, 1);
    },
    onSearch(search, loading) {
      if (search.length) {
        loading(true);
        this.searchVoucher(loading, search, this);
      } else {
        loading(true);
        setTimeout(() => {
          this.options = [];
          this.getVoucher();
          loading(false);
        }, 500);
      }
    },
    searchVoucher: _.debounce((loading, search, vm) => {
      vm.$store
        .dispatch("vouchers/getData", {
          uuid: "",
          params: { supplier_uuid: vm.supplier_uuid, search },
        })
        .then((response) => {
          vm.options = [];
          vm.options = response.data.data.data.filter((el) => !el.is_used);
          loading(false);
        });
    }, 300),
    getVoucher() {
      this.$store
        .dispatch("vouchers/getData", {
          uuid: "",
          params: { supplier_uuid: this.supplier_uuid },
        })
        .then((result) => {
          this.options = result.data.data.data.filter((el) => !el.is_used);
          this.voucherOptions = result.data.data.data.filter((el) => !el.is_used);
        })
        .catch((err) => console.log(err));
    },
    getDetailVoucher(id) {
      this.$store
        .dispatch("vouchers/getData", { uuid: id, params: "" })
        .then((result) => { this.detail_voucher = result.data.data; })
        .catch((err) => console.log(err));
    },
    detailPreOrder(uuid, isStockOnSupplier) {
      if (isStockOnSupplier) {
        this.$store
          .dispatch("stockWarehouse/getStockSupplier", { uuid, params: "" })
          .then((result) => {
            if (result.data.data) {
              this.result = result.data.data;
              this.$bvModal.show("modal-detail-supplier");
            }
          })
          .catch((err) => console.log(err));
      }
      if (!isStockOnSupplier) {
        this.$store
          .dispatch("stockWarehouse/getData", { uuid, params: "" })
          .then((result) => {
            if (result.data.data) {
              this.result = result.data.data;
              this.$bvModal.show("modal-detail-supplier");
            }
          })
          .catch((err) => console.log(err));
      }
    },
    preparePayload() {
      const form = new FormData();
      const filteredData = Object.values(this.selectedItems);
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < filteredData.length; index++) {
        const element = { ...filteredData[index], is_checked: 1 };
        if (element.uuid) {
          // eslint-disable-next-line no-restricted-syntax
          for (const key in element) {
            // eslint-disable-next-line no-prototype-builtins
            if (element.hasOwnProperty(key) && element !== "") {
              const items = element[key];
              form.append(`items[${index}][${key}]`, items);
            }
          }
        }
      }
      return form;
    },
    handleVoucherChange(index, newVouchers) {
      const oldVouchers = this.vouchers[index] || [];
      const safeNewVouchers = newVouchers || [];
      const added = safeNewVouchers.filter(
        (v) => !oldVouchers.some((ov) => ov.uuid === v.uuid)
      );
      const removed = oldVouchers.filter(
        (ov) => !safeNewVouchers.some((v) => v.uuid === ov.uuid)
      );
      added.forEach((voucher) => {
        const idx = this.options.findIndex((o) => o.uuid === voucher.uuid);
        if (idx !== -1) this.options.splice(idx, 1);
      });
      removed.forEach((voucher) => {
        if (!this.options.some((o) => o.uuid === voucher.uuid)) {
          this.options.push(voucher);
        }
      });
      this.$set(this.vouchers, index, safeNewVouchers);
      if (this.formPayload.items[index]) {
        this.formPayload.items[index].voucher_uuid = safeNewVouchers.map((v) => v.uuid);
        this.syncSelectedItem(index);
        const unpaid = this.formPayload.items[index].unpaid;
        if (safeNewVouchers.length > 0) {
          const voucherTotal = safeNewVouchers.reduce(
            (acc, el) => acc + (el.amount || 0), 0
          );
          this.formPayload.items[index].amount = Math.max(0, unpaid - voucherTotal);
        } else {
          this.formPayload.items[index].amount = unpaid;
        }
      }
      this.calculateTotalVoucher();
      this.calculateTotal();
    },
    handleAmountChange(index) {
      const item = this.formPayload.items[index];
      let amount = this.preformatFloat(item.amount);
      if (amount > item.unpaid) {
        amount = item.unpaid;
        this.$set(this.formPayload.items[index], "amount", amount);
      }
      this.$nextTick(() => {
        this.syncSelectedItem(index);
        this.calculateTotal();
      });
    },
    handleSelectionChange(index) {
      const formItem = this.formPayload.items[index];
      const row = this.items[index];
      formItem.is_checked = formItem.uuid ? 1 : 0;
      if (formItem.uuid) {
        this.$set(this.selectedItems, row.uuid, { 
          ...formItem, 
          uuid: row.uuid,
          no_po: row.no_po,
          branch: row.branch,
          due_date: row.due_date,
          uuid_po: row.uuid_po,
          is_stock_on_supplier: row.is_stock_on_supplier,
        });
      } else {
        this.$delete(this.selectedItems, row.uuid);
      }
      this.calculateTotal();
    },
    calculateTotalVoucher() {
      let total = 0;
      Object.values(this.selectedItems).forEach((row) => {
        if (row && row.length) {
          row.forEach((v) => (total += v.amount || 0));
        }
      });
      this.totalVoucher = total;
    },
    calculateTotal() {
      let total = 0;
      Object.values(this.selectedItems).forEach((item) => {
        const amount =
          typeof item.amount === "string"
            ? parseFloat(item.amount.replace(/\./g, "").replace(",", ".") || 0)
            : item.amount;
        total += amount;
      });
      this.total = Math.ceil(total);
      this.$store.commit("supplier/setTotal", this.total);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

/* ── Root ─────────────────────────────────── */
.debt-payment-root {
  position: relative;
}

/* ── Loading Overlay ─────────────────────── */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-card {
  background: #fff;
  border-radius: 12px;
  padding: 28px 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.loading-text {
  font-size: 13.5px;
  color: #6e6b7b;
  font-weight: 500;
  margin: 0;
}

/* ── Header ──────────────────────────────── */
.dp-header {
  background: #fff;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover {
    background: #f4f4f8;
  }
}

/* ── Table ───────────────────────────────── */
.dp-table {
  &.table {
    thead th {
      background: #f8f8fc !important;
      color: #6e6b7b !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      padding: 12px 8px !important;
      border-bottom: 2px solid #e9ecef !important;
      border-top: none !important;
      white-space: nowrap;
    }
    tbody tr {
      transition: background 0.12s;
      &:hover {
        background: #fafafa !important;
      }
      td {
        font-size: 13px !important;
        color: #3d3d4e !important;
        padding: 10px 8px !important;
        border-bottom: 1px solid #f0f0f5 !important;
        vertical-align: middle;
      }
    }
  }
}

/* ── Due date badge ──────────────────────── */
.due-info-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
}
.due-badge-warn     { background: rgba(255,159,67,.13); color: #d08010; }
.due-badge-danger   { background: rgba(234,84,85,.1);  color: #c0392b; }
.due-badge-critical { background: rgba(234,84,85,.18); color: #a12020; }
.due-badge-safe     { background: rgba(40,199,111,.12); color: #1a9352; }

/* ── Unpaid value ─────────────────────────── */
.unpaid-val {
  font-weight: 700;
  color: #ea5455;
  white-space: nowrap;
}

/* ── No.PO link ───────────────────────────── */
.text-blue {
  color: #004ad7;
  cursor: pointer;
  &:hover { text-decoration: underline; }
}

/* ── Footer ───────────────────────────────── */
.footer__purchase {
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  z-index: 1000;
}

/* ── Save / Pay buttons ───────────────────── */
.btn-save-draft {
  background: #fff !important;
  color: #6e6b7b !important;
  border: 1.5px solid #d0d0da !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: all 0.18s;
  &:hover {
    background: #f4f4f8 !important;
    border-color: #b0b0be !important;
  }
}
.btn-pay-action {
  background: linear-gradient(135deg, #28c76f, #20a75e) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(40, 199, 111, 0.28);
  transition: all 0.18s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 14px rgba(40, 199, 111, 0.38);
  }
  &:disabled {
    opacity: 0.55 !important;
    transform: none !important;
    box-shadow: none !important;
    cursor: not-allowed;
  }
}

/* ── Pagination ───────────────────────────── */
.pagination {
  margin-bottom: 0 !important;
}
.pagination .page-link {
  padding: 0.4rem 0.75rem;
}

/* ── Dropdowns ────────────────────────────── */
#utang {
  .btn-outline-secondary.dropdown-toggle::after { background-image: unset; }
  .dropdown-toggle::after { width: 0; height: 0; }
  .delivery-numbers .dropdown-toggle::after { width: 10px; height: 10px; }
  .dropdown-menu, .b-dropdown-form { padding: 0; }
  #dropdown-form-voucher {
    &:focus { box-shadow: unset; }
    &::placeholder { font-size: 14px; }
  }
}

/* ── Loading fade ─────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── Mobile ───────────────────────────────── */
@media (max-width: 768px) {
  .footer__purchase .row {
    flex-direction: column;
    gap: 10px;
  }
  .footer__purchase .col-lg-4 {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
  .footer__purchase .d-flex.justify-content-end {
    justify-content: flex-start !important;
    flex-wrap: wrap;
    gap: 8px;
  }
  .btn-pay-action,
  .btn-save-draft {
    width: 100%;
    justify-content: center;
  }
}
</style>
